/**
 * Volume Discount Table Component
 *
 * This component handles the display and functionality of volume-based pricing tables.
 * It integrates with Clay's volume pricing and provides:
 * - Dynamic table restructuring from vertical to horizontal layout
 * - Real-time price updates based on quantity selection
 * - Automatic tier highlighting
 * - Fallback contact message when pricing data is unavailable
 *
 * @author Craftparts Development Team
 * @version 2.0.0
 */

// ============================================================================
// Global State
// ============================================================================

/**
 * Prevents multiple component instances from processing simultaneously
 * @type {boolean}
 */
let isProcessing = false;

// ============================================================================
// Component Definition
// ============================================================================

if (!customElements.get('volume-discount-table')) {
  customElements.define(
    'volume-discount-table',
    class VolumeDiscountTable extends HTMLElement {
      // ======================================================================
      // Constructor & Initial Setup
      // ======================================================================

      constructor() {
        super();
        this.initializeConfig();
        this.initializeState();
        this.initializeReferences();
      }

      /**
       * Initializes component configuration settings
       */
      initializeConfig() {
        this.config = {
          // Retry settings for Clay container detection
          maxRetryAttempts: 10,
          retryInterval: 200, // milliseconds
          useMutationObserver: true,

          // DOM selectors
          selectors: {
            volumeContainer: '.clay-quantity-grid-table-container',
            addToCartButton: '.product-form__submit',
            buttonsContainer: '.product-form__buttons',
            quantityInput: '.product-form__input input[name="quantity"], input[name="quantity"]',
            priceWrapper: '.price-wrapper'
          },

          // CSS class names
          classes: {
            // Container classes
            container: 'volume-discount-table__container',
            containerVisible: 'volume-discount-table__container--visible',
            header: 'volume-discount-table__header',
            wrapper: 'volume-discount-table__wrapper',

            // Table cell classes
            label: 'volume-discount-table__label',
            priceCell: 'volume-discount-table__price-cell',
            priceCellActive: 'volume-discount-table__price-cell--active',

            // Contact message classes
            contact: 'volume-discount-contact',
            contactHidden: 'volume-discount-contact--hidden',
            contactMessage: 'volume-discount-contact__message',

            // Price display classes
            volumePrice: 'volume-discount-price',

            // State classes
            initialized: 'volume-discount-table--initialized',
            loading: 'volume-discount-loading',
            loadingHidden: 'volume-discount-loading--hidden',
            loadingSpinner: 'volume-discount-loading__spinner'
          }
        };
      }

      /**
       * Initializes component state variables
       */
      initializeState() {
        this.state = {
          // DOM element references
          volumePricingContainer: null,
          customTableWrapper: null,
          contactMessage: null,
          loadingIndicator: null,
          quantityInput: null,
          priceWrapper: null,

          // Retry mechanism state
          retryAttempts: 0,
          checkInterval: null,

          // Current variant data
          currentVariant: null,

          // Initialization flag
          isInitialized: false
        };
      }

      /**
       * Initializes event handler and observer references
       */
      initializeReferences() {
        // Bound event handlers for proper cleanup
        this.boundHandleQuantityChange = null;
        this.variantChangeUnsubscriber = null;

        // Timeout for debouncing variant changes
        this.variantChangeTimeout = null;

        // MutationObserver for detecting Clay injection
        this.clayObserver = null;
      }

      // ======================================================================
      // Lifecycle Methods
      // ======================================================================

      /**
       * Called when component is added to the DOM
       */
      connectedCallback() {
        this.positionElement();
        this.removeStaleElements();
        this.createLoadingIndicator();
        this.createContactMessage();
        this.loadInitialVariant();

        // Immediately show compare at price if available (don't wait for volume table)
        this.cacheElements();
        this.showCompareAtPriceOnly();

        this.init();
        this.subscribeToVariantChanges();
      }

      /**
       * Called when component is removed from the DOM
       */
      disconnectedCallback() {
        this.unsubscribeFromVariantChanges();
        this.cleanup();
      }

      // ======================================================================
      // Initialization
      // ======================================================================

      /**
       * Loads initial variant data from the variant picker
       */
      loadInitialVariant() {
        const variantDataScript = document.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
        if (!variantDataScript) return;

        try {
          const variants = JSON.parse(variantDataScript.textContent);
          if (!Array.isArray(variants) || variants.length === 0) return;

          // Check URL for variant parameter
          const urlParams = new URLSearchParams(window.location.search);
          const variantIdFromUrl = urlParams.get('variant');

          if (variantIdFromUrl) {
            const variantById = variants.find(v => v.id.toString() === variantIdFromUrl);
            if (variantById) {
              this.state.currentVariant = variantById;
              return;
            }
          }

          // Fallback to first available variant
          this.state.currentVariant = variants.find(v => v.available) || variants[0];
        } catch (error) {
          console.error('[VolumeDiscountTable] Error loading variant:', error);
        }
      }

      /**
       * Initializes the component and starts waiting for Clay container
       */
      init() {
        if (this.state.isInitialized) {
          return;
        }

        this.state.isInitialized = true;

        // Only show loading if we don't have compare at price to display
        const hasCompareAtPrice = this.state.currentVariant?.compare_at_price;
        if (!hasCompareAtPrice) {
          this.showLoading();
        }

        this.waitForVolumePricingContainer();
      }

      /**
       * Waits for Clay's volume pricing container to be injected
       * Uses MutationObserver by default, falls back to polling if unavailable
       */
      waitForVolumePricingContainer() {
        const useMutationObserver =
          this.config.useMutationObserver &&
          typeof MutationObserver !== 'undefined';

        if (useMutationObserver) {
          this.waitWithMutationObserver();
        } else {
          this.waitWithPolling();
        }
      }

      /**
       * Waits for container using MutationObserver (preferred method)
       */
      waitWithMutationObserver() {
        // Check if container already exists
        const existingContainer = document.querySelector(
          this.config.selectors.volumeContainer
        );

        if (existingContainer) {
          this.handleContainerFound();
          return;
        }

        // Set up fallback timeout
        const maxWaitTime =
          this.config.maxRetryAttempts * this.config.retryInterval;

        const fallbackTimeout = setTimeout(() => {
          this.disconnectClayObserver();
          this.handleNoVolumeContainer();
        }, maxWaitTime);

        // Create observer
        this.clayObserver = new MutationObserver(() => {
          const container = document.querySelector(
            this.config.selectors.volumeContainer
          );

          if (container) {
            clearTimeout(fallbackTimeout);
            this.disconnectClayObserver();
            this.handleContainerFound();
          }
        });

        // Start observing
        this.clayObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }

      /**
       * Waits for container using interval polling (fallback method)
       */
      waitWithPolling() {
        // Clear any existing interval
        this.clearCheckInterval();

        this.state.checkInterval = setInterval(() => {
          this.state.retryAttempts++;

          const container = document.querySelector(
            this.config.selectors.volumeContainer
          );

          if (container) {
            this.clearCheckInterval();
            this.handleContainerFound();
          } else if (this.state.retryAttempts >= this.config.maxRetryAttempts) {
            this.clearCheckInterval();
            this.handleNoVolumeContainer();
          }
        }, this.config.retryInterval);
      }

      /**
       * Handles successful detection of Clay container
       */
      handleContainerFound() {
        this.removeDuplicateContainers();

        this.state.volumePricingContainer = document.querySelector(
          this.config.selectors.volumeContainer
        );

        if (this.state.volumePricingContainer) {
          this.setupTable();
        }
      }

      /**
       * Handles case when Clay B2B volume container is not found.
       * Falls back to showing compare-at price if available, otherwise regular price.
       */
      handleNoVolumeContainer() {
        this.hideLoading();
        this.hideContactMessage();
        this.cacheElements();
        this.showCompareAtPriceOnly();
      }

      /**
       * Disconnects the MutationObserver if active
       */
      disconnectClayObserver() {
        if (this.clayObserver) {
          this.clayObserver.disconnect();
          this.clayObserver = null;
        }
      }

      /**
       * Clears the polling interval if active
       */
      clearCheckInterval() {
        if (this.state.checkInterval) {
          clearInterval(this.state.checkInterval);
          this.state.checkInterval = null;
        }
      }

      // ======================================================================
      // Table Setup and Restructuring
      // ======================================================================

      /**
       * Main setup method - restructures and displays the pricing table
       */
      setupTable() {
        // Prevent duplicate processing
        if (isProcessing) {
          return;
        }

        if (this.isAlreadyInitialized()) {
          return;
        }

        isProcessing = true;

        try {
          const hasValidData = this.validateTableData();

          if (!hasValidData) {
            this.handleMissingData();
            return;
          }

          this.executeSetup();
        } finally {
          isProcessing = false;
        }
      }

      /**
       * Checks if container is already initialized
       * @returns {boolean}
       */
      isAlreadyInitialized() {
        return this.state.volumePricingContainer.classList.contains(
          this.config.classes.initialized
        );
      }

      /**
       * Validates that the Clay table contains at least one tier with a real discount.
       * Clay table structure per row: [Quantity, Price, Save%]
       * Returns false (hides the table) if every tier shows 0% savings.
       * @returns {boolean}
       */
      validateTableData() {
        const originalTable = this.state.volumePricingContainer.querySelector('table');
        const rows = originalTable?.querySelectorAll('tbody tr') || [];

        if (rows.length === 0) return false;

        const hasAnyDiscount = Array.from(rows).some(row => {
          const cells = row.querySelectorAll('td');
          if (cells.length < 3) return false;

          const savingsPercent = parseFloat(cells[2].textContent.replace(/[^0-9.]/g, ''));
          return !isNaN(savingsPercent) && savingsPercent > 0;
        });

        return hasAnyDiscount;
      }

      /**
       * Handles case when the table has no valid discount data.
       * Hides the Clay container and falls back to compare-at price display.
       */
      handleMissingData() {
        this.state.volumePricingContainer.style.display = 'none';
        this.hideLoading();
        this.hideContactMessage();
        this.cacheElements();
        this.showCompareAtPriceOnly();
      }

      /**
       * Executes the main setup sequence
       */
      executeSetup() {
        this.hideLoading();
        this.hideContactMessage();
        this.cacheElements();
        this.restructureTable();
        this.createTableWrapper();
        this.setupEventListeners();
        this.updatePriceDisplay();
        this.markAsInitialized();
      }

      /**
       * Marks container as initialized
       */
      markAsInitialized() {
        this.state.volumePricingContainer.classList.add(
          this.config.classes.initialized
        );
      }

      /**
       * Caches frequently accessed DOM elements
       */
      cacheElements() {
        this.state.quantityInput = document.querySelector(
          this.config.selectors.quantityInput
        );

        const priceId = this.dataset.section
          ? `#price-${this.dataset.section}`
          : '[id^="price-"]';

        this.state.priceWrapper = document.querySelector(
          `${priceId} ${this.config.selectors.priceWrapper}`
        );
      }

      /**
       * Restructures the table from vertical to horizontal layout
       */
      restructureTable() {
        const originalTable = this.state.volumePricingContainer.querySelector('table');

        if (!originalTable) {
          return;
        }

        const tableData = this.extractTableData(originalTable);

        if (!tableData) {
          return;
        }

        // Add tier 1 with original price if Clay data doesn't start at 1
        this.prependOriginalPriceTier(tableData);

        const newTable = this.buildHorizontalTable(tableData);
        originalTable.replaceWith(newTable);

        // Store quantities for tier calculation
        this.storeQuantitiesData(newTable, tableData.quantities);
      }

      /**
       * Prepends a tier for quantity 1 with original price if Clay data doesn't start at 1
       * @param {Object} tableData - Table data object to modify
       */
      prependOriginalPriceTier(tableData) {
        if (!tableData || !tableData.quantities || tableData.quantities.length === 0) {
          return;
        }

        // Check if first tier starts at 1
        const firstQty = parseInt(tableData.quantities[0].replace('+', ''), 10);

        if (firstQty > 1 && this.state.currentVariant && this.state.currentVariant.price) {
          // Format original price to match Clay's format (e.g., "$6.00")
          const originalPrice = this.formatPrice(this.state.currentVariant.price);

          // Prepend tier 1 data
          tableData.quantities.unshift('1');
          tableData.prices.unshift(originalPrice);
          tableData.discounts.unshift('0%');
        }
      }

      /**
       * Extracts data from the original vertical table
       * @param {HTMLTableElement} table
       * @returns {Object|null} Table data object
       */
      extractTableData(table) {
        const rows = table.querySelectorAll('tbody tr');

        if (rows.length === 0) {
          return null;
        }

        const quantities = [];
        const prices = [];
        const discounts = [];

        rows.forEach((row) => {
          const cells = row.querySelectorAll('td');

          if (cells.length >= 2) {
            quantities.push(cells[0].textContent.trim());
            prices.push(cells[1].textContent.trim());
            discounts.push(
              cells.length >= 3 ? cells[2].textContent.trim() : '0%'
            );
          }
        });

        return { quantities, prices, discounts };
      }

      /**
       * Builds a horizontal table from extracted data
       * @param {Object} data - Table data
       * @returns {HTMLTableElement}
       */
      buildHorizontalTable(data) {
        const { quantities, prices, discounts } = data;
        const table = document.createElement('table');

        const rows = [
          {
            label: 'Quantity',
            cells: this.buildQuantityCells(quantities)
          },
          {
            label: 'Save',
            cells: this.buildDataCells(discounts)
          },
          {
            label: 'Price Per pack',
            cells: this.buildPriceCells(prices)
          }
        ];

        const rowsHTML = rows
          .map(row => `
            <tr>
              <td class="${this.config.classes.label}">${row.label}</td>
              ${row.cells}
            </tr>
          `)
          .join('');

        table.innerHTML = `<tbody>${rowsHTML}</tbody>`;

        return table;
      }

      /**
       * Builds quantity cells with tier data attributes
       * @param {string[]} quantities
       * @returns {string} HTML string
       */
      buildQuantityCells(quantities) {
        return quantities
          .map((qty, index) => {
            const displayQty = this.formatQuantityDisplay(
              qty,
              index,
              quantities
            );
            return `<td data-tier="${index}">${displayQty}</td>`;
          })
          .join('');
      }

      /**
       * Builds generic data cells
       * @param {string[]} data
       * @returns {string} HTML string
       */
      buildDataCells(data) {
        return data
          .map((value, index) => `<td data-tier="${index}">${value}</td>`)
          .join('');
      }

      /**
       * Builds price cells with special styling class
       * @param {string[]} prices
       * @returns {string} HTML string
       */
      buildPriceCells(prices) {
        return prices
          .map((price, index) => {
            const classes = this.config.classes.priceCell;
            return `<td data-tier="${index}" class="${classes}">${price}</td>`;
          })
          .join('');
      }

      /**
       * Formats quantity display as ranges (e.g., "1-9", "10-24", "25+")
       * @param {string} qty - Current quantity
       * @param {number} index - Current index
       * @param {string[]} quantities - All quantities
       * @returns {string} Formatted display string
       */
      formatQuantityDisplay(qty, index, quantities) {
        const isLastColumn = index === quantities.length - 1;
        const currentQty = parseInt(qty.replace('+', ''), 10);

        if (isLastColumn) {
          return `${currentQty}+`;
        }

        const nextQty = parseInt(
          quantities[index + 1].replace('+', ''),
          10
        );
        const rangeEnd = nextQty - 1;

        return currentQty === rangeEnd
          ? `${currentQty}`
          : `${currentQty}-${rangeEnd}`;
      }

      /**
       * Stores quantities data on table element for later reference
       * @param {HTMLTableElement} table
       * @param {string[]} quantities
       */
      storeQuantitiesData(table, quantities) {
        try {
          table.dataset.quantities = JSON.stringify(quantities);
        } catch (error) {
          console.error(
            '[VolumeDiscountTable] Error storing quantities:',
            error
          );
        }
      }

      // ======================================================================
      // Table Wrapper and UI Creation
      // ======================================================================

      /**
       * Creates and inserts the custom table wrapper with header
       */
      createTableWrapper() {
        const insertionPoint = this.findInsertionPoint();

        if (!insertionPoint) {
          return;
        }

        const wrapper = this.buildWrapper();
        const header = this.buildHeader();
        const tableWrapper = this.buildTableWrapper();

        wrapper.appendChild(header);
        wrapper.appendChild(tableWrapper);

        this.insertWrapper(wrapper, insertionPoint);
        this.hideOriginalContainer();
        this.storeWrapperReference(wrapper);
        this.animateIn(wrapper);
      }

      /**
       * Finds the DOM insertion point for the wrapper
       * @returns {Element|null}
       */
      findInsertionPoint() {
        const addToCartButton = document.querySelector(
          this.config.selectors.addToCartButton
        );

        if (!addToCartButton) {
          return null;
        }

        return addToCartButton.closest(
          this.config.selectors.buttonsContainer
        );
      }

      /**
       * Builds the main wrapper element
       * @returns {HTMLDivElement}
       */
      buildWrapper() {
        return this.createElement('div', this.config.classes.container);
      }

      /**
       * Builds the header element
       * @returns {HTMLDivElement}
       */
      buildHeader() {
        const header = this.createElement('div', this.config.classes.header);
        header.textContent = 'Volume Discount Chart';
        return header;
      }

      /**
       * Builds the table wrapper and moves table into it
       * @returns {HTMLDivElement}
       */
      buildTableWrapper() {
        const wrapper = this.createElement('div', this.config.classes.wrapper);
        const table = this.state.volumePricingContainer.querySelector('table');

        if (table) {
          wrapper.appendChild(table);
        }

        return wrapper;
      }

      /**
       * Inserts wrapper into DOM
       * @param {HTMLDivElement} wrapper
       * @param {Element} insertionPoint
       */
      insertWrapper(wrapper, insertionPoint) {
        insertionPoint.parentNode.insertBefore(
          wrapper,
          insertionPoint.nextSibling
        );
      }

      /**
       * Hides the original Clay container
       */
      hideOriginalContainer() {
        this.state.volumePricingContainer.style.display = 'none';
      }

      /**
       * Stores reference to wrapper for later access
       * @param {HTMLDivElement} wrapper
       */
      storeWrapperReference(wrapper) {
        this.state.customTableWrapper = wrapper;
      }

      /**
       * Animates wrapper into view
       * @param {HTMLDivElement} wrapper
       */
      animateIn(wrapper) {
        requestAnimationFrame(() => {
          wrapper.classList.add(this.config.classes.containerVisible);
        });
      }

      // ======================================================================
      // DOM Element Positioning
      // ======================================================================

      /**
       * Positions the custom element in the correct location in the DOM
       */
      positionElement() {
        const addToCartButton = document.querySelector(
          this.config.selectors.addToCartButton
        );

        if (!addToCartButton) {
          return;
        }

        const buttonsContainer = addToCartButton.closest(
          this.config.selectors.buttonsContainer
        );

        if (!buttonsContainer) {
          return;
        }

        const targetParent = buttonsContainer.parentNode;
        const nextSibling = buttonsContainer.nextSibling;

        // Only move if not already in correct position
        const isInCorrectPosition =
          this.parentNode === targetParent &&
          this.previousSibling === buttonsContainer;

        if (!isInCorrectPosition) {
          targetParent.insertBefore(this, nextSibling);
        }
      }

      // ======================================================================
      // Loading Indicator
      // ======================================================================

      /**
       * Creates loading indicator UI element
       */
      createLoadingIndicator() {
        if (this.state.loadingIndicator) {
          return;
        }

        const loadingDiv = this.createElement(
          'div',
          this.config.classes.loading
        );

        const spinner = this.createElement(
          'div',
          this.config.classes.loadingSpinner
        );

        loadingDiv.appendChild(spinner);
        this.appendChild(loadingDiv);

        this.state.loadingIndicator = loadingDiv;
      }

      /**
       * Shows loading indicator
       */
      showLoading() {
        this.state.loadingIndicator?.classList.remove(
          this.config.classes.loadingHidden
        );
      }

      /**
       * Hides loading indicator
       */
      hideLoading() {
        this.state.loadingIndicator?.classList.add(
          this.config.classes.loadingHidden
        );
      }

      // ======================================================================
      // Contact Message
      // ======================================================================

      /**
       * Creates contact message UI element
       */
      createContactMessage() {
        if (this.state.contactMessage) {
          return;
        }

        const contactDiv = this.createElement(
          'div',
          `${this.config.classes.contact} ${this.config.classes.contactHidden}`
        );

        const paragraph = this.createElement(
          'p',
          this.config.classes.contactMessage
        );

        const textNode = document.createTextNode('Need More? ');
        const link = document.createElement('a');
        link.href = '/pages/contact';
        link.textContent = 'Contact Us';

        paragraph.appendChild(textNode);
        paragraph.appendChild(link);
        contactDiv.appendChild(paragraph);

        this.appendChild(contactDiv);
        this.state.contactMessage = contactDiv;
      }

      /**
       * Shows contact message
       */
      showContactMessage() {
        this.state.contactMessage?.classList.remove(
          this.config.classes.contactHidden
        );
      }

      /**
       * Hides contact message
       */
      hideContactMessage() {
        this.state.contactMessage?.classList.add(
          this.config.classes.contactHidden
        );
      }

      // ======================================================================
      // Price Display Updates
      // ======================================================================

      /**
       * Updates price display based on current quantity
       */
      updatePriceDisplay() {
        if (!this.canUpdatePrice()) {
          return;
        }

        const currentQty = this.getCurrentQuantity();
        const tierIndex = this.calculateTierIndex(currentQty);

        this.updateTableHighlight(tierIndex);
        this.updateProductPrice(tierIndex);
      }

      /**
       * Checks if price update is possible
       * @returns {boolean}
       */
      canUpdatePrice() {
        return !!(
          this.state.quantityInput &&
          this.state.customTableWrapper
        );
      }

      /**
       * Gets current quantity from input
       * @returns {number}
       */
      getCurrentQuantity() {
        return parseInt(this.state.quantityInput.value, 10) || 1;
      }

      /**
       * Calculates which pricing tier applies to given quantity
       * @param {number} quantity
       * @returns {number} Tier index
       */
      calculateTierIndex(quantity) {
        const table = this.state.customTableWrapper?.querySelector('table');
        const quantitiesData = table?.dataset.quantities;

        if (!quantitiesData) {
          return 0;
        }

        const quantities = this.parseJSON(quantitiesData, []);

        if (!Array.isArray(quantities) || quantities.length === 0) {
          return 0;
        }

        let tierIndex = 0;

        for (let i = 0; i < quantities.length; i++) {
          const minQty = parseInt(quantities[i].replace('+', ''), 10);

          if (quantity >= minQty) {
            tierIndex = i;
          }
        }

        return tierIndex;
      }

      /**
       * Updates table to highlight active tier
       * @param {number} tierIndex
       */
      updateTableHighlight(tierIndex) {
        if (!this.state.customTableWrapper) {
          return;
        }

        const priceCells = this.state.customTableWrapper.querySelectorAll(
          `.${this.config.classes.priceCell}`
        );

        // Remove active class from all cells
        priceCells.forEach(cell => {
          cell.classList.remove(this.config.classes.priceCellActive);
        });

        // Add active class to current tier
        const activeCell = this.state.customTableWrapper.querySelector(
          `.${this.config.classes.priceCell}[data-tier="${tierIndex}"]`
        );

        if (activeCell) {
          activeCell.classList.add(this.config.classes.priceCellActive);

          // Auto-scroll to active cell if not fully visible
          this.scrollActiveCellIntoView(activeCell);
        }
      }

      /**
       * Scrolls the active cell into view if it's not fully visible
       * @param {HTMLElement} activeCell - The active price cell element
       */
      scrollActiveCellIntoView(activeCell) {
        const tableWrapper = this.state.customTableWrapper.querySelector(
          `.${this.config.classes.wrapper}`
        );

        if (!tableWrapper) {
          return;
        }

        // Check if horizontal scrolling is present
        const hasHorizontalScroll = tableWrapper.scrollWidth > tableWrapper.clientWidth;

        if (!hasHorizontalScroll) {
          return;
        }

        // Get the tier index from the active cell
        const tierIndex = parseInt(activeCell.getAttribute('data-tier'), 10);

        // If first cell (tier 0), scroll all the way to the left
        if (tierIndex === 0) {
          tableWrapper.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
          return;
        }

        // Get dimensions and positions
        const wrapperRect = tableWrapper.getBoundingClientRect();
        const cellRect = activeCell.getBoundingClientRect();

        // Calculate if cell is visible
        const cellLeft = cellRect.left - wrapperRect.left + tableWrapper.scrollLeft;
        const cellRight = cellLeft + cellRect.width;
        const viewportLeft = tableWrapper.scrollLeft;
        const viewportRight = viewportLeft + tableWrapper.clientWidth;

        // Add padding for better UX (20px on each side)
        const scrollPadding = 20;

        // Determine if scrolling is needed
        let scrollTo = null;

        if (cellLeft < viewportLeft + scrollPadding) {
          // Cell is hidden on the left
          scrollTo = Math.max(0, cellLeft - scrollPadding);
        } else if (cellRight > viewportRight - scrollPadding) {
          // Cell is hidden on the right
          scrollTo = cellRight - tableWrapper.clientWidth + scrollPadding;
        }

        // Perform smooth scroll if needed
        if (scrollTo !== null) {
          tableWrapper.scrollTo({
            left: scrollTo,
            behavior: 'smooth'
          });
        }
      }

      /**
       * Updates product price display based on tier
       * @param {number} tierIndex
       */
      updateProductPrice(tierIndex) {
        if (!this.state.priceWrapper || !this.state.customTableWrapper) {
          return;
        }

        const priceCells = this.state.customTableWrapper.querySelectorAll(
          `.${this.config.classes.priceCell}`
        );

        if (priceCells.length === 0) {
          return;
        }

        const currentPriceText = priceCells[tierIndex].textContent.trim();
        const currentPrice = this.parsePrice(currentPriceText);

        // Get first tier price (original price before volume discount)
        const firstTierPriceText = priceCells[0].textContent.trim();
        const firstTierPrice = this.parsePrice(firstTierPriceText);

        // Check if variant has compare_at_price
        const compareAtPrice = this.state.currentVariant?.compare_at_price;
        const compareAtPriceInDollars = compareAtPrice ? compareAtPrice / 100 : null;

        // Determine if we should show a discount
        let hasDiscount = false;
        let originalPriceText = '';

        // Priority 1: Compare at price (if exists and is higher than current price)
        let showBadge = false;
        if (compareAtPriceInDollars && currentPrice < compareAtPriceInDollars) {
          hasDiscount = true;
          originalPriceText = this.formatPrice(compareAtPrice);
          showBadge = true; // Show badge for compare at price
        }
        // Priority 2: Volume discount (tier > 0 and price is lower than first tier)
        else if (tierIndex > 0 && currentPrice < firstTierPrice) {
          hasDiscount = true;
          originalPriceText = firstTierPriceText;
          showBadge = false; // Don't show badge for volume discount
        }

        if (hasDiscount) {
          this.showDiscountPrice(originalPriceText, currentPriceText, showBadge);
        } else {
          this.showRegularPrice();
        }
      }

      /**
       * Shows compare at price when there's no volume table
       */
      showCompareAtPriceOnly() {
        if (!this.state.priceWrapper || !this.state.currentVariant) {
          return;
        }

        const compareAtPrice = this.state.currentVariant.compare_at_price;
        const variantPrice = this.state.currentVariant.price;

        // Only show if compare at price exists and is higher than variant price
        if (compareAtPrice && variantPrice < compareAtPrice) {
          const compareAtPriceText = this.formatPrice(compareAtPrice);
          const variantPriceText = this.formatPrice(variantPrice);
          this.showDiscountPrice(compareAtPriceText, variantPriceText, true);
        } else {
          this.showRegularPrice();
        }
      }

      /**
       * Formats price from cents to currency string
       * @param {number} priceInCents - Price in cents
       * @returns {string} Formatted price string (e.g., "$12.99")
       */
      formatPrice(priceInCents) {
        const dollars = priceInCents / 100;
        return `$${dollars.toFixed(2)}`;
      }

      /**
       * Parses price string to number
       * @param {string} priceText
       * @returns {number}
       */
      parsePrice(priceText) {
        return parseFloat(priceText.replace(/[^0-9.]/g, ''));
      }

      /**
       * Shows discount price with strikethrough original
       * @param {string} originalPrice
       * @param {string} discountPrice
       * @param {boolean} showBadge - Whether to show SALE badge
       */
      showDiscountPrice(originalPrice, discountPrice, showBadge = false) {
        // Hide original price container
        const originalPriceContainer = this.state.priceWrapper.querySelector('.price');
        if (originalPriceContainer) {
          originalPriceContainer.style.display = 'none';
        }

        // Get or create custom price container
        let customPriceDiv = this.state.priceWrapper.querySelector(
          `.${this.config.classes.volumePrice}`
        );

        if (!customPriceDiv) {
          customPriceDiv = this.createDiscountPriceContainer();
          this.state.priceWrapper.appendChild(customPriceDiv);
        }

        // Build price structure
        this.buildDiscountPriceStructure(
          customPriceDiv,
          originalPrice,
          discountPrice,
          showBadge
        );

        customPriceDiv.style.display = 'block';
      }

      /**
       * Creates discount price container element
       * @returns {HTMLDivElement}
       */
      createDiscountPriceContainer() {
        const div = document.createElement('div');
        div.className = [
          this.config.classes.volumePrice,
          'price',
          'price--large',
          'price--on-sale',
          'price--show-badge'
        ].join(' ');
        return div;
      }

      /**
       * Builds discount price DOM structure
       * @param {HTMLDivElement} container
       * @param {string} originalPrice
       * @param {string} discountPrice
       * @param {boolean} showBadge - Whether to show SALE badge
       */
      buildDiscountPriceStructure(container, originalPrice, discountPrice, showBadge = false) {
        // Clear existing content
        container.innerHTML = '';

        // Create main price container
        const priceContainer = document.createElement('div');
        priceContainer.className = 'price__container';

        // Create sale price section
        const salePriceDiv = document.createElement('div');
        salePriceDiv.className = 'price__sale';

        // Sale price label (screen reader only)
        const saleLabel = this.createScreenReaderLabel('Sale price');

        // Sale price value
        const salePrice = document.createElement('span');
        salePrice.className = 'price-item price-item--sale price-item--last';
        salePrice.textContent = discountPrice;

        // Regular price label (screen reader only)
        const regularLabel = this.createScreenReaderLabel('Regular price');

        // Regular price with strikethrough
        const regularPriceContainer = document.createElement('span');
        const regularPrice = document.createElement('s');
        regularPrice.className = 'price-item price-item--regular';
        regularPrice.textContent = originalPrice;
        regularPriceContainer.appendChild(regularPrice);

        // Assemble structure
        salePriceDiv.appendChild(saleLabel);
        salePriceDiv.appendChild(salePrice);
        salePriceDiv.appendChild(regularLabel);
        salePriceDiv.appendChild(regularPriceContainer);

        priceContainer.appendChild(salePriceDiv);
        container.appendChild(priceContainer);

        // Add SALE badge only if showBadge is true
        if (showBadge) {
          const saleBadge = document.createElement('span');
          saleBadge.className = 'badge price__badge-sale color-accent-2';
          saleBadge.textContent = 'Sale';
          container.appendChild(saleBadge);
        }
      }

      /**
       * Creates a screen reader only label
       * @param {string} text
       * @returns {HTMLSpanElement}
       */
      createScreenReaderLabel(text) {
        const label = document.createElement('span');
        label.className = 'visually-hidden visually-hidden--inline';
        label.textContent = text;
        return label;
      }

      /**
       * Shows regular price (hides discount price)
       */
      showRegularPrice() {
        if (!this.state.priceWrapper) {
          return;
        }

        const originalPriceContainer = this.state.priceWrapper.querySelector('.price');
        if (originalPriceContainer) {
          originalPriceContainer.style.display = 'block';
        }

        const customPriceDiv = this.state.priceWrapper.querySelector(
          `.${this.config.classes.volumePrice}`
        );

        if (customPriceDiv) {
          customPriceDiv.style.display = 'none';
        }
      }

      // ======================================================================
      // Event Handling
      // ======================================================================

      /**
       * Sets up event listeners for quantity changes
       */
      setupEventListeners() {
        if (!this.state.quantityInput) {
          return;
        }

        this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);

        this.state.quantityInput.addEventListener(
          'input',
          this.boundHandleQuantityChange
        );

        this.state.quantityInput.addEventListener(
          'change',
          this.boundHandleQuantityChange
        );
      }

      /**
       * Handles quantity input changes
       */
      handleQuantityChange() {
        this.updatePriceDisplay();
      }

      /**
       * Subscribes to Shopify variant change events
       */
      subscribeToVariantChanges() {
        const hasSubscribeAPI =
          typeof subscribe !== 'undefined' &&
          typeof PUB_SUB_EVENTS !== 'undefined';

        if (!hasSubscribeAPI) {
          return;
        }

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }

      /**
       * Unsubscribes from variant change events
       */
      unsubscribeFromVariantChanges() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = null;
        }
      }

      /**
       * Handles product variant changes
       * @param {Object} event - Variant change event containing variant data
       */
      handleVariantChange(event) {
        // Store new variant data
        if (event && event.data && event.data.variant) {
          this.state.currentVariant = event.data.variant;
        }

        // Cancel pending variant change timeout
        if (this.variantChangeTimeout) {
          clearTimeout(this.variantChangeTimeout);
          this.variantChangeTimeout = null;
        }

        // Clean up current state
        this.cleanup();
        this.hideContactMessage();
        this.hideLoading();
        this.state.retryAttempts = 0;
        this.state.isInitialized = false;

        // Immediately show compare at price for new variant
        this.cacheElements();
        this.showCompareAtPriceOnly();

        // Reinitialize after delay
        this.variantChangeTimeout = setTimeout(() => {
          this.variantChangeTimeout = null;
          this.init();
        }, 100);
      }

      // ======================================================================
      // Cleanup and Utilities
      // ======================================================================

      /**
       * Removes stale elements from previous component instances
       */
      removeStaleElements() {
        // Remove old custom table wrappers
        const customWrappers = document.querySelectorAll(
          `.${this.config.classes.container}`
        );
        customWrappers.forEach(wrapper => wrapper.remove());

        // Reset initialized Clay containers
        const volumePricingContainers = document.querySelectorAll(
          this.config.selectors.volumeContainer
        );
        volumePricingContainers.forEach(container => {
          container.classList.remove(this.config.classes.initialized);
        });
      }

      /**
       * Removes duplicate Clay containers
       */
      removeDuplicateContainers() {
        // Keep only first Clay container
        const containers = document.querySelectorAll(
          this.config.selectors.volumeContainer
        );
        containers.forEach((container, index) => {
          if (index > 0) {
            container.remove();
          }
        });

        // Remove extra custom wrappers
        const customWrappers = document.querySelectorAll(
          `.${this.config.classes.container}`
        );
        customWrappers.forEach(wrapper => {
          if (wrapper !== this.state.customTableWrapper) {
            wrapper.remove();
          }
        });
      }

      /**
       * Comprehensive cleanup of component resources
       */
      cleanup() {
        this.clearCheckInterval();
        this.disconnectClayObserver();
        this.clearVariantChangeTimeout();
        this.removeEventListeners();
        this.clearTableHighlighting();
        this.removeCustomTableWrapper();
        this.cleanupInitializedContainers();
        this.resetState();
        this.resetGlobalFlag();
      }

      /**
       * Clears variant change timeout
       */
      clearVariantChangeTimeout() {
        if (this.variantChangeTimeout) {
          clearTimeout(this.variantChangeTimeout);
          this.variantChangeTimeout = null;
        }
      }

      /**
       * Removes event listeners
       */
      removeEventListeners() {
        if (this.state.quantityInput && this.boundHandleQuantityChange) {
          this.state.quantityInput.removeEventListener(
            'input',
            this.boundHandleQuantityChange
          );
          this.state.quantityInput.removeEventListener(
            'change',
            this.boundHandleQuantityChange
          );
          this.boundHandleQuantityChange = null;
        }
      }

      /**
       * Clears table highlighting
       */
      clearTableHighlighting() {
        const priceCells = this.state.customTableWrapper?.querySelectorAll(
          `.${this.config.classes.priceCell}`
        );

        priceCells?.forEach(cell => {
          cell.classList.remove(this.config.classes.priceCellActive);
        });
      }

      /**
       * Removes custom table wrapper from DOM
       */
      removeCustomTableWrapper() {
        if (this.state.customTableWrapper) {
          this.state.customTableWrapper.remove();
          this.state.customTableWrapper = null;
        }
      }

      /**
       * Cleans up initialized Clay containers
       */
      cleanupInitializedContainers() {
        const containers = document.querySelectorAll(
          this.config.selectors.volumeContainer
        );

        containers.forEach(container => {
          if (container.classList.contains(this.config.classes.initialized)) {
            container.remove();
          }
        });
      }

      /**
       * Resets component state
       */
      resetState() {
        this.state.volumePricingContainer = null;
        this.state.quantityInput = null;
        this.state.priceWrapper = null;
      }

      /**
       * Resets global processing flag
       */
      resetGlobalFlag() {
        isProcessing = false;
      }

      /**
       * Helper method to create element with class
       * @param {string} tag - HTML tag name
       * @param {string} className - CSS class name(s)
       * @returns {HTMLElement}
       */
      createElement(tag, className) {
        const element = document.createElement(tag);
        element.className = className;
        return element;
      }

      /**
       * Safely parses JSON with fallback
       * @param {string} jsonString
       * @param {*} fallback
       * @returns {*}
       */
      parseJSON(jsonString, fallback = null) {
        try {
          return JSON.parse(jsonString);
        } catch (error) {
          console.error('[VolumeDiscountTable] JSON parse error:', error);
          return fallback;
        }
      }
    }
  );
}
